import { GetMyUpcomingReservationsResponseDto } from "@/api/generated/generatedTypes";
import { EventReservationFormValues } from "@/pages/EventReservation.page";
import { Box, Group, Stack, Text, UnstyledButton } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { IconMeat, IconMessage, IconWallet, IconWorld } from "@tabler/icons-react";
import { DinnerQuestionStep, getStepLabel } from "../constants";

interface ReturningUserOverviewProps {
  latestReservation: GetMyUpcomingReservationsResponseDto;
  onCategoryClick: (step: DinnerQuestionStep) => void;
  form: UseFormReturnType<
    EventReservationFormValues,
    (values: EventReservationFormValues) => EventReservationFormValues
  >;
}

const LANGUAGE_LABELS: Record<string, string> = {
  KOREAN: "한국어",
  ENGLISH: "English",
  ALL: "한국어 & English",
};

const COST_LABELS: Record<string, string> = {
  LOW: "₩",
  MEDIUM: "₩₩",
  HIGH: "₩₩₩",
};

const DIETARY_LABELS: Record<string, string> = {
  NONE: "없음",
  VEGAN: "비건",
  VEGETARIAN: "채식",
  GLUTEN_FREE: "글루텐 프리",
  OTHER: "기타",
};

export const getStepIcon = (step: DinnerQuestionStep): React.ReactNode => {
  switch (step) {
    case DinnerQuestionStep.LANGUAGE:
      return <IconWorld size={24} />;
    case DinnerQuestionStep.COST:
      return <IconWallet />;
    case DinnerQuestionStep.DIETARY:
      return <IconMeat />;
    case DinnerQuestionStep.MESSAGE:
      return <IconMessage />;
  }
};
export function ReturningUserOverview({
  latestReservation,
  onCategoryClick,
  form,
}: ReturningUserOverviewProps) {
  const getCurrentValue = (step: DinnerQuestionStep): string => {
    switch (step) {
      case DinnerQuestionStep.LANGUAGE: {
        const formValue = form.values.dinnerPreference.preferredLanguage;
        const value = formValue || latestReservation.preferredLanguage;
        return value ? LANGUAGE_LABELS[value] || value : "미설정";
      }
      case DinnerQuestionStep.COST: {
        const formValue = form.values.dinnerPreference.preferredCost;
        const value = formValue || latestReservation.preferredCost;
        return value ? COST_LABELS[value] || value : "미설정";
      }
      case DinnerQuestionStep.DIETARY: {
        const formRestrictions = form.values.dinnerPreference.dietaryRestrictions;
        const formOtherText = form.values.dinnerPreference.dietaryRestrictionsOtherText;

        const restrictions =
          formRestrictions && formRestrictions.length > 0
            ? formRestrictions
            : latestReservation.dietaryRestrictionSlugs;
        const otherText = formOtherText || latestReservation.dietaryRestrictionsOtherText;

        if (!restrictions || restrictions.length === 0) {
          return "없음";
        }
        const labels = restrictions.map((slug: string) => DIETARY_LABELS[slug] || slug);
        if (otherText) {
          return `${labels.join(", ")} (${otherText})`;
        }
        return labels.join(", ");
      }
      case DinnerQuestionStep.MESSAGE: {
        const formValue = form.values.event.messageForGuests;
        const value = formValue || latestReservation.messageForGuests;
        return value || "작성 안 함";
      }
    }
  };

  const topRowSteps = [
    DinnerQuestionStep.LANGUAGE,
    DinnerQuestionStep.COST,
    DinnerQuestionStep.DIETARY,
  ];
  const bottomRowStep = DinnerQuestionStep.MESSAGE;

  const renderStepCard = (step: DinnerQuestionStep, fullWidth: boolean = false) => (
    <UnstyledButton
      key={step}
      onClick={() => onCategoryClick(step)}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        padding: "16px",
        borderRadius: "8px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        cursor: "pointer",
        transition: "all 0.2s",
        flex: fullWidth ? undefined : 1,
        width: fullWidth ? "100%" : undefined,
        minHeight: "100px",
      }}
      styles={{
        root: {
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            transform: "translateY(-2px)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          },
        },
      }}
    >
      <Text size="2rem">{getStepIcon(step)}</Text>
      <Stack gap={4} align="center">
        <Text size="sm" fw={600} c="white">
          {getStepLabel(step)}
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          {getCurrentValue(step)}
        </Text>
      </Stack>
    </UnstyledButton>
  );

  return (
    <Stack gap="md">
      <Box
        style={{
          border: "1px solid rgba(255, 255, 255, 0.2)",
          borderRadius: "12px",
          padding: "16px",
          backgroundColor: "rgba(255, 255, 255, 0.05)",
        }}
      >
        <Stack gap="md">
          {/* Top row: Language, Cost, Dietary */}
          <Group gap="md" grow align="stretch">
            {topRowSteps.map((step) => renderStepCard(step, false))}
          </Group>

          {/* Bottom row: Message (full width) */}
          {renderStepCard(bottomRowStep, true)}
        </Stack>
      </Box>
    </Stack>
  );
}
